"use client"

import { useLocale, useTranslations } from "next-intl"
import type { ComponentProps } from "react"

import { TEditorIcon } from "../../ui/display/TEditorIcon"
import { TText } from "../../ui/display/TText"
import { TTooltip } from "../../ui/tooltip/TTooltip"
import { toTitleCase } from "../utils/format-label"
import { tWithFallback } from "../utils/i18n"
import { useDynamicFormLabelContext } from "./form-label-context"

type TextVariant = NonNullable<ComponentProps<typeof TText>["variant"]>

const ICON_SIZE_BY_VARIANT: Record<TextVariant, number> = {
	page: 38,
	display: 32,
	headline: 26,
	title: 22,
	subtitle: 20,
	content: 18,
	caption: 16,
	help: 14,
	tiny: 12
}

interface DynamicFormLabelProps {
	name: string
	/** 스키마의 title — i18n 번역이 없을 때 toTitleCase(name)보다 우선한다 */
	title?: string
	required?: boolean
	/** 명시적으로 지정 시 Provider의 hideRequiredMark를 override */
	hideRequiredMark?: boolean
	description?: string
	variant?: TextVariant
	color?: ComponentProps<typeof TText>["color"]
}

export function DynamicFormLabel({ name, title, required, hideRequiredMark: hideRequiredMarkProp, description, variant = "caption", color = "secondary" }: DynamicFormLabelProps) {
	const t = useTranslations()
	const locale = useLocale()

	const { namespace, hideRequiredMark: hideRequiredMarkContext } = useDynamicFormLabelContext()
	const hideRequiredMark = hideRequiredMarkProp ?? hideRequiredMarkContext

	const labelKey = `${namespace}.fields.${name}.label`
	const translatedLabel = tWithFallback(t, locale, labelKey, title ?? toTitleCase(name))

	const descriptionKey = `${namespace}.fields.${name}.description`
	const translatedDescription = tWithFallback(t, locale, descriptionKey, description ?? "")

	return (
		<div className="flex items-center gap-1.5">
			<TText variant={variant} color={color} bold>
				{translatedLabel}
				{required && !hideRequiredMark && <span>*</span>}
			</TText>
			{translatedDescription && (
				<TTooltip title={translatedDescription} className="max-w-[360px]">
					<TEditorIcon
						name="information"
						size={ICON_SIZE_BY_VARIANT[variant]}
						color="text-secondary"
						className="group-hover:opacity-100 opacity-0 transition-opacity duration-300"
					/>
				</TTooltip>
			)}
		</div>
	)
}
